// Token + chatId come from the URL query string (set by the Telegram deep
// link or by a saved bookmark). We persist both to localStorage on first
// load so the user can subsequently type just `https://mc.thelandmuleos.org`
// without the token in the URL. Dwight rule 2026-05-14: prioritize UX
// (typeable bare URL after first auth) over the marginal blast-radius
// concern of localStorage vs sessionStorage. The token is already exposed
// in URL query strings and bookmarks; localStorage isn't materially worse.

const url = new URL(window.location.href);

let cachedToken = url.searchParams.get('token') || '';
if (cachedToken) {
  try { localStorage.setItem('claudeclaw.token', cachedToken); } catch {}
} else {
  // Read from localStorage first (persistent), fall back to sessionStorage
  // for users with a recent-session token from before this change shipped.
  try { cachedToken = localStorage.getItem('claudeclaw.token') || sessionStorage.getItem('claudeclaw.token') || ''; } catch {}
}

let cachedChatId = url.searchParams.get('chatId') || '';
if (cachedChatId) {
  try { localStorage.setItem('claudeclaw.chatId', cachedChatId); } catch {}
} else {
  try { cachedChatId = localStorage.getItem('claudeclaw.chatId') || sessionStorage.getItem('claudeclaw.chatId') || ''; } catch {}
}

export const dashboardToken = cachedToken;
export const chatId = cachedChatId;

function withToken(path: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}token=${encodeURIComponent(dashboardToken)}`;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

// All fetches send credentials (cookies) AND, if we still have a legacy
// token cached, also tack it onto the URL. Either path authenticates on
// the server side.
const FETCH_OPTS_BASE: RequestInit = { credentials: 'include' };

/** On any 401 from an API call, redirect to /login (preserving the next path). */
function handle401AndMaybeRedirect(path: string) {
  // Avoid redirect loops — already on login or change-password? Don't bounce.
  const cur = window.location.pathname;
  if (cur === '/login' || cur === '/change-password') return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  // Use replace so the broken page doesn't end up in back-history
  window.location.replace(`/login?next=${next}`);
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch(withToken(path), { ...FETCH_OPTS_BASE, method: 'GET' });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body, `GET ${path} failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(withToken(path), {
    ...FETCH_OPTS_BASE,
    method: 'POST',
    headers: body ? { 'content-type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errBody, `POST ${path} failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPatch<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(withToken(path), {
    ...FETCH_OPTS_BASE,
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errBody, `PATCH ${path} failed: ${res.status}`);
  }
  return res.json();
}

// Multipart upload — caller passes a FormData instance.
export async function apiUpload<T = unknown>(path: string, form: FormData): Promise<T> {
  const res = await fetch(withToken(path), { ...FETCH_OPTS_BASE, method: 'POST', body: form });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errBody, `UPLOAD ${path} failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPut<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(withToken(path), {
    ...FETCH_OPTS_BASE,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errBody, `PUT ${path} failed: ${res.status}`);
  }
  return res.json();
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const res = await fetch(withToken(path), { ...FETCH_OPTS_BASE, method: 'DELETE' });
  if (res.status === 401) handle401AndMaybeRedirect(path);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body, `DELETE ${path} failed: ${res.status}`);
  }
  return res.json();
}

/** Log out: hits /api/auth/logout to clear the session cookie + redirects. */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { ...FETCH_OPTS_BASE, method: 'POST' });
  } catch {
    // Ignore network error — still clear local state and redirect
  }
  try {
    localStorage.removeItem('claudeclaw.token');
    sessionStorage.removeItem('claudeclaw.token');
  } catch {}
  window.location.replace('/login');
}

export function tokenizedSseUrl(path: string): string {
  return withToken(path);
}

// Vite dev runs on :5173 and proxies /api/* and /warroom/text to the
// backend on :3141. The legacy voice room at /warroom?mode=voice can't
// be proxied (it shares a path prefix with the v2 SPA route), so links
// that go to legacy pages must point at the backend origin in dev.
const BACKEND_ORIGIN = (import.meta as any).env?.DEV ? 'http://localhost:3141' : '';

export function legacyUrl(path: string): string {
  return BACKEND_ORIGIN + path;
}
